import { useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { Logo } from "../components/Logo";

const CONTACT = "admin@pinscher-botb.com";

/** Brand emblem for the hero. Falls back to the shield mark until
 *  web/public/pinscher-logo.png is added. */
function HeroEmblem() {
  const [failed, setFailed] = useState(false);
  return (
    <div className="emblem-wrap" aria-hidden="true">
      {failed ? (
        <div className="emblem-fallback">
          <Logo size={240} />
        </div>
      ) : (
        <img
          className="emblem"
          src="/pinscher-logo.png"
          alt="PINSCHER — AI Red Team"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

/* tiny inline icon set */
const Icon = {
  target: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    </svg>
  ),
  brain: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M9 4a3 3 0 0 0-3 3 3 3 0 0 0-1 5.8A2.5 2.5 0 0 0 7 17a2.5 2.5 0 0 0 5 .5V5a2 2 0 0 0-3-1Z" />
      <path d="M15 4a3 3 0 0 1 3 3 3 3 0 0 1 1 5.8A2.5 2.5 0 0 1 17 17a2.5 2.5 0 0 1-5 .5" />
    </svg>
  ),
  shield: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M12 3 4 6v6c0 5 3.5 7.5 8 9 4.5-1.5 8-4 8-9V6Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  ),
  pulse: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M3 12h4l2 6 4-14 2 8h6" />
    </svg>
  ),
  layers: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="m12 3 9 5-9 5-9-5Z" />
      <path d="m3 13 9 5 9-5" />
    </svg>
  ),
  bolt: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
      <path d="M13 2 4 14h7l-1 8 9-12h-7Z" />
    </svg>
  ),
  arrow: (
    <svg className="arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  check: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
      <path d="m5 12 5 5L20 7" />
    </svg>
  ),
};

export function Home() {
  return (
    <>
      {/* ---------------- HERO ---------------- */}
      <section className="hero">
        <div className="hero-glow" />
        <div className="container">
          <div className="hero-layout">
            <div className="hero-inner">
              <Reveal>
                <span className="badge">
                  <span className="dot" />
                  ACCEPTED · DEF CON 34 — BATTLE OF THE BOTS: VISHING
                </span>
              </Reveal>
              <Reveal delay={1}>
                <h1 className="h-hero">
                  The <span className="grad">AI red team</span> for the human layer.
                </h1>
              </Reveal>
              <Reveal delay={2}>
                <p className="lead">
                  PINSCHER builds autonomous vishing agents — AI that calls, adapts, and improvises like a
                  real social engineer. We pressure-test the human layer, then train the people who have to
                  hold the line.
                </p>
              </Reveal>
              <Reveal delay={3}>
                <div className="hero-cta">
                  <Link to="/investors" className="btn btn-primary">
                    For investors {Icon.arrow}
                  </Link>
                  <a className="btn btn-ghost" href="/#how">
                    How it works
                  </a>
                </div>
              </Reveal>
              <Reveal delay={4}>
                <div className="hero-meta">
                  <span>
                    <b>Autonomous vishing</b> · voice-first
                  </span>
                  <span>
                    <b>Hybrid architecture</b> · purpose-built
                  </span>
                  <span>
                    <b>Authorized</b> · adversarial research only
                  </span>
                </div>
              </Reveal>
            </div>
            <Reveal delay={2}>
              <HeroEmblem />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- LOGO / TRUST STRIP ---------------- */}
      <div className="container">
        <div className="divider" />
      </div>

      {/* ---------------- APPROACH ---------------- */}
      <section className="section" id="approach">
        <div className="container">
          <div className="section-head">
            <Reveal>
              <span className="eyebrow">The problem</span>
            </Reveal>
            <Reveal delay={1}>
              <h2 className="h-sec">
                Attackers stopped breaking in. <br />
                They started <span className="grad">logging in.</span>
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="lead">
                The human element factors into the majority of breaches — a convincing phone call (vishing),
                a well-timed message, a moment of misplaced trust. Yet most organizations test and train their
                people once a year, if ever, with static simulations that look nothing like a real adversary.
                PINSCHER closes that gap from both sides: pressure-test the human layer with a realistic AI
                adversary, then train the operators who have to hold the line.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-3">
            {[
              {
                ic: Icon.target,
                n: "01",
                h: "Pressure-test the human layer",
                p: "Run realistic, consent-based social engineering scenarios against your own people and processes — continuously, not as a once-a-year checkbox.",
              },
              {
                ic: Icon.brain,
                n: "02",
                h: "Adapts like a real adversary",
                p: "The agent reasons about context, reads the room, and changes tactics mid-conversation — exposing the failure modes static phishing tests never reach.",
              },
              {
                ic: Icon.shield,
                n: "03",
                h: "Train the blue team",
                p: "Every engagement becomes targeted operator training — realistic reps against a genuine AI social engineer, plus high-level guidance on where trust broke and how to harden it.",
              },
            ].map((c, i) => (
              <Reveal key={c.n} delay={(i + 1) as 1 | 2 | 3}>
                <div className="card">
                  <span className="num">{c.n}</span>
                  <div className="ic">{c.ic}</div>
                  <h3>{c.h}</h3>
                  <p>{c.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TECHNOLOGY (vague on architecture) ---------------- */}
      <section className="section" id="how">
        <div className="container">
          <div className="split">
            <div>
              <Reveal>
                <span className="eyebrow">The technology</span>
              </Reveal>
              <Reveal delay={1}>
                <h2 className="h-sec" style={{ margin: "16px 0 18px" }}>
                  A <span className="grad">hybrid architecture</span> with novel methods at its core.
                </h2>
              </Reveal>
              <Reveal delay={2}>
                <p className="lead">
                  We keep the how behind closed doors. What matters is the outcome: an agent that thinks and
                  improvises like a real social engineer, finds exactly where trust breaks, and turns every
                  engagement into training that makes your people harder to fool.
                </p>
              </Reveal>
              <ul className="feature-list">
                <Reveal as="li" delay={1}>
                  <span className="chk">{Icon.check}</span>
                  <div>
                    <b>Hybrid architecture, proprietary methods.</b>
                    <p>Purpose-built for human-layer attacks — not a generic chatbot wired to a phone line. We don't publish how it works.</p>
                  </div>
                </Reveal>
                <Reveal as="li" delay={2}>
                  <span className="chk">{Icon.check}</span>
                  <div>
                    <b>Convincing under real pressure.</b>
                    <p>Holds a natural, multi-turn conversation and adapts mid-call — surfacing the failure modes static phishing tests never reach.</p>
                  </div>
                </Reveal>
                <Reveal as="li" delay={3}>
                  <span className="chk">{Icon.check}</span>
                  <div>
                    <b>Built to defend.</b>
                    <p>Authorized, consent-based engagements that turn findings into operator training — offense in service of the blue team.</p>
                  </div>
                </Reveal>
              </ul>
            </div>

            <Reveal delay={2}>
              <div className="panel" aria-hidden="true">
                <div className="panel-bar">
                  <span className="led r" />
                  <span className="led y" />
                  <span className="led g" />
                  <span className="tab">pinscher://engagement · live</span>
                </div>
                <div className="panel-body">
                  <span className="ln">
                    <span className="c-dim"># authorized engagement — session 0x4f</span>
                  </span>
                  <span className="ln">
                    <span className="c-key">objective</span> :: <span className="c-sig">build_rapport → establish_pretext</span>
                  </span>
                  <span className="ln">
                    <span className="c-key">mode</span> :: <span className="c-amb">adaptive</span> · latency{" "}
                    <span className="c-sig">low</span>
                  </span>
                  <span className="ln">
                    <span className="c-dim">scope</span> ──▶ authorized · consent on file
                  </span>
                  <span className="ln">
                    <span className="c-dim">outcome</span> ──▶ findings → operator training
                  </span>
                  <span className="ln"> </span>
                  <span className="ln">
                    <span className="c-sig">▸ posture</span> confident · <span className="c-sig">calibrated</span>
                  </span>
                  <span className="ln">
                    <span className="c-sig">▸ status</span> holding context across turns
                  </span>
                  <span className="ln">
                    <span className="c-amb">▸ finding</span> trust boundary identified @ step 7
                  </span>
                  <span className="ln">
                    <span className="c-dim">$</span> <span className="cursor" />
                  </span>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal>
            <div className="tags">
              <span className="tag sig">hybrid architecture</span>
              <span className="tag sig">novel methods</span>
              <span className="tag">autonomous vishing</span>
              <span className="tag">adaptive tactics</span>
              <span className="tag">authorized</span>
              <span className="tag sig">red-team → blue-team</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- DEFCON ---------------- */}
      <section className="section-tight" id="defcon">
        <div className="container">
          <Reveal>
            <div className="defcon">
              <div className="scan" />
              <span className="eyebrow" style={{ color: "var(--amber)" }}>
                <span style={{ background: "var(--amber)" }} />
                The proving ground
              </span>
              <div className="split" style={{ marginTop: 22, alignItems: "start" }}>
                <div>
                  <h2 className="h-sec" style={{ marginBottom: 18 }}>
                    Accepted to <span className="amber">Battle of the Bots: Vishing Edition.</span>
                  </h2>
                  <p className="lead" style={{ maxWidth: "52ch" }}>
                    Run by the Social Engineering Community at DEF CON 34, Battle of the Bots: Vishing Edition is
                    where autonomous social engineering stops being theory — teams field AI agents that place
                    live vishing calls to real targets, scored by judges in real time. It's a selective field,
                    and acceptance is itself a signal. PINSCHER earned a seat.
                  </p>
                  <p className="muted" style={{ marginTop: 18, maxWidth: "52ch" }}>
                    For us it's more than a competition. It's third-party proof that our agent performs under
                    pressure — and a forcing function that keeps us at the frontier of where this technology is
                    actually going.
                  </p>
                  <div className="hero-cta" style={{ marginTop: 28 }}>
                    <Link to="/investors" className="btn btn-primary">
                      Why this matters for investors {Icon.arrow}
                    </Link>
                  </div>
                </div>
                <div className="grid" style={{ gap: 14 }}>
                  {[
                    { ic: Icon.pulse, h: "Live, adversarial", p: "Tested against real defenders in real time — not a benchmark, a battle." },
                    { ic: Icon.layers, h: "Selective field", p: "A competitive bar to even compete. Acceptance is signal." },
                    { ic: Icon.bolt, h: "Frontier pace", p: "Where the next decade of social engineering is being defined." },
                  ].map((c) => (
                    <div className="card" key={c.h} style={{ padding: 22 }}>
                      <div className="ic" style={{ marginBottom: 14, color: "var(--amber)", background: "rgba(185,107,255,0.09)", borderColor: "rgba(185,107,255,0.22)" }}>
                        {c.ic}
                      </div>
                      <h3 style={{ fontSize: "1.05rem" }}>{c.h}</h3>
                      <p>{c.p}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------------- STATS ---------------- */}
      <section className="section-tight">
        <div className="container">
          <div className="grid grid-4">
            {[
              { v: <span className="grad">~60%</span>, k: "of breaches involve a human element (Verizon DBIR)" },
              { v: <span className="grad">+442%</span>, k: "rise in vishing detections, H1→H2 2024 (CrowdStrike)" },
              { v: <span className="amber">DC34</span>, k: "Battle of the Bots: Vishing — accepted" },
              { v: "1", k: "agent. hybrid architecture, novel methods" },
            ].map((s, i) => (
              <Reveal key={s.k} delay={(i + 1) as 1 | 2 | 3 | 4}>
                <div className="stat">
                  <div className="v">{s.v}</div>
                  <div className="k">{s.k}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="section-tight">
        <div className="container">
          <Reveal>
            <div className="cta-band">
              <span className="eyebrow center" style={{ justifyContent: "center" }}>
                Get in touch
              </span>
              <h2 className="h-sec" style={{ margin: "18px 0 16px" }}>
                Harden the human layer with us.
              </h2>
              <p className="lead" style={{ margin: "0 auto 30px", textAlign: "center" }}>
                Whether you're a security leader who wants to see PINSCHER run, or an investor sizing the
                opportunity — we'd like to talk.
              </p>
              <div className="hero-cta" style={{ justifyContent: "center" }}>
                <a className="btn btn-primary" href={`mailto:${CONTACT}?subject=PINSCHER%20inquiry`}>
                  {CONTACT}
                </a>
                <Link to="/investors" className="btn btn-ghost">
                  Investor overview {Icon.arrow}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
